/**
 * The topbar's "courses, bundles, people".
 *
 * Server-side: titles are encrypted at rest, so there is nothing on this side
 * to filter. `people` comes back empty for somebody who cannot teach — a member
 * has no screen that shows another member — and this page renders what it is
 * given rather than deciding that itself.
 */

import { learn } from '../api';
import { Avatar, BundleTile, CourseTile, TileGrid } from '../components/Bits';
import { Empty, ErrorNote, Loading, PageTitle, Panel, PanelHead } from '../components/Chrome';
import { useRouter } from '../router';
import { meta, monoPlain } from '../theme';
import { useLoad } from '../useLoad';

export default function Search() {
  const { query } = useRouter();
  const q = query.get('q') ?? '';

  const { data, error, loading } = useLoad(
    () => (q.trim() ? learn.search(q) : Promise.resolve(null)),
    'Could not search.',
    q
  );

  if (error) return <ErrorNote>{error}</ErrorNote>;
  if (!q.trim()) {
    return (
      <>
        <PageTitle title="Search." />
        <Empty line="Search from the box at the top." />
      </>
    );
  }
  if (!data) return loading ? <Loading /> : null;

  const nothing =
    data.courses.length === 0 && data.bundles.length === 0 && data.people.length === 0;

  return (
    <>
      <PageTitle
        title="Search."
        meta={meta(
          `“${data.query}”`,
          `${data.courses.length + data.bundles.length + data.people.length} results`
        )}
      />

      {nothing && <Empty line="Nothing matched." />}

      {data.courses.length > 0 && (
        <Panel style={{ display: 'grid', gap: 16 }}>
          <PanelHead title="Courses" />
          <TileGrid>
            {data.courses.map((c) => (
              <CourseTile key={c.id} course={c} />
            ))}
          </TileGrid>
        </Panel>
      )}

      {data.bundles.length > 0 && (
        <Panel style={{ display: 'grid', gap: 16 }}>
          <PanelHead title="Bundles" />
          <TileGrid>
            {data.bundles.map((b) => (
              <BundleTile key={b.id} bundle={b} />
            ))}
          </TileGrid>
        </Panel>
      )}

      {data.people.length > 0 && (
        <Panel style={{ display: 'grid', gap: 10 }}>
          <PanelHead title="People" />
          {data.people.map((person) => (
            <span
              key={person.userId}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}
            >
              <Avatar name={person.name} handle={person.handle} />
              <span style={{ fontSize: 14, color: 'var(--text-strong)' }}>{person.name}</span>
              <span style={monoPlain}>
                {meta(person.handle ? `@${person.handle}` : null, person.team)}
              </span>
            </span>
          ))}
        </Panel>
      )}
    </>
  );
}
